import React from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonList
} from '@ionic/react';
import { bagHandle, pricetags, reorderFour, bulb } from 'ionicons/icons';
import { IssueFlaggerTable } from '../parameters-schema';
import { EditorMode } from './types';
import EditorSelectionItem from './editor-selection-item';
import EditorSelectionSection from './editor-selection-section';
import InferenceRuleList from './inference-rule-list';

interface EditorSelectionModalProps {
  isOpen: boolean;
  inferenceRules: IssueFlaggerTable[];
  currentMode: EditorMode;
  onDismiss: () => void;
  onSelectMode: (mode: EditorMode) => void;
  onCreateInferenceRule: () => void;
}

const EditorSelectionModal: React.FC<EditorSelectionModalProps> = ({
  isOpen,
  inferenceRules,
  currentMode,
  onDismiss,
  onSelectMode,
  onCreateInferenceRule
}) => {
  const handleSelect = (mode: EditorMode) => {
    onSelectMode(mode);
    onDismiss();
  };

  const handleCreateInferenceRule = () => {
    onCreateInferenceRule();
    onDismiss();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss} className="editor-selection-modal">
      <IonHeader>
        <IonToolbar>
          <IonTitle>Select what to edit</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>Close</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="editor-selection-layout">
          <div className="editor-selection-column editor-selection-column--rules">
            <EditorSelectionSection title="Inference Rules" badgeText={`${inferenceRules.length}`}>
              <InferenceRuleList
                inferenceRules={inferenceRules}
                activeRuleId={currentMode.type === 'inferenceRule' ? currentMode.id : undefined}
                listMaxHeight="62vh"
                onSelectRule={(id) => handleSelect({ type: 'inferenceRule', id })}
                onCreateRule={handleCreateInferenceRule}
              />
            </EditorSelectionSection>
          </div>

          <div className="editor-selection-column">
            <EditorSelectionSection title="Products">
              <IonList>
                <EditorSelectionItem
                  label="Product List"
                  description="Manage product tags"
                  icon={bagHandle}
                  isActive={currentMode.type === 'products'}
                  onClick={() => handleSelect({ type: 'products' })}
                />
              </IonList>
            </EditorSelectionSection>

            <EditorSelectionSection title="Tags">
              <IonList>
                <EditorSelectionItem
                  label="Tags"
                  description="Manage additional tags"
                  icon={pricetags}
                  isActive={currentMode.type === 'nonProductTags'}
                  onClick={() => handleSelect({ type: 'nonProductTags' })}
                />
              </IonList>
            </EditorSelectionSection>

            <EditorSelectionSection title="Issue Priority">
              <IonList>
                <EditorSelectionItem
                  label="Issue Priority Order"
                  description="Arrange rule evaluation order"
                  icon={reorderFour}
                  isActive={currentMode.type === 'issueTriage'}
                  onClick={() => handleSelect({ type: 'issueTriage' })}
                />
              </IonList>
            </EditorSelectionSection>

            <EditorSelectionSection title="Strategy Recommendation">
              <IonList>
                <EditorSelectionItem
                  label="Strategy Recommendations"
                  description="Edit strategy decision tree"
                  icon={bulb}
                  isActive={currentMode.type === 'strategyRecommendation'}
                  onClick={() => handleSelect({ type: 'strategyRecommendation' })}
                />
              </IonList>
            </EditorSelectionSection>
          </div>
        </div>
      </IonContent>

      <style>{`
        ion-modal.editor-selection-modal {
          --width: 80vw;
          --height: 80vh;
          --max-width: 80vw;
          --max-height: 80vh;
        }

        ion-modal.editor-selection-modal::part(content) {
          border-radius: 12px;
        }

        .editor-selection-layout {
          display: grid;
          grid-template-columns: minmax(280px, 36%) minmax(360px, 1fr);
          gap: 1.5rem;
          padding: 1.25rem 1.5rem 1.5rem;
        }

        .editor-selection-column {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .editor-selection-column--rules {
          align-self: start;
        }

        .editor-selection-item {
          --background: #f8f8f8;
          --background-activated: #e9eef8;
          border-radius: 10px;
          margin: 0.4rem 0;
        }

        .editor-selection-item--active {
          --background: rgba(56, 128, 255, 0.12);
          border: 1px solid rgba(56, 128, 255, 0.35);
        }

        .editor-selection-item__icon {
          width: 36px;
          height: 36px;
          border-radius: 9px;
          background: linear-gradient(180deg, #ffffff 0%, #ececec 100%);
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 0.75rem;
          color: var(--ion-color-primary);
        }

        .inference-rule-list .searchbar-input {
          font-size: 0.9rem;
        }

        .inference-rule-list__create {
          margin: 0.5rem 0 0.75rem;
          --border-radius: 10px;
        }

        @media (max-width: 900px) {
          ion-modal.editor-selection-modal {
            --width: 92vw;
            --height: 92vh;
            --max-width: 92vw;
            --max-height: 92vh;
          }

          .editor-selection-layout {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 600px) {
          ion-modal.editor-selection-modal {
            --width: 96vw;
            --height: 96vh;
            --max-width: 96vw;
            --max-height: 96vh;
          }
        }
      `}</style>
    </IonModal>
  );
};

export default EditorSelectionModal;
